'use client';

import { useCallback } from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'framer-motion';
import CinematicScene from './CinematicScene';
import BgOrbs from './BgOrbs';
import { tryCtaScrollToFinal } from '../lib/tryNavigation';

/**
 * SCENE 10: Можно вдвоём.
 *
 * Структура:
 *  1. Cinematic-блок (CinematicScene) — заголовок, текст, переход дальше.
 *  2. "Живые подборки" — горизонтальный ряд stories-style карточек с
 *     реальными vote-ссылками. Пользователь может кликнуть и увидеть как
 *     работает голосование с воther стороны.
 */

type Story = {
  title: string;
  subtitle: string;
  href: string;
  accent: string;
  cover: string;
};

const STORIES: Story[] = [
  {
    title: 'Оскар 2026',
    subtitle: 'Посмотреть фильм из номинантов',
    href: 'https://favbox.app/vote/k9ebaxJG',
    accent: '#c25a4c',
    cover: '/landing/covers/story-oscar.webp',
  },
  {
    title: 'Поездка на выходные',
    subtitle: 'Несколько идей куда',
    href: 'https://favbox.app/vote/3wnTJKed',
    accent: '#7b8be5',
    cover: '/landing/covers/story-trip.webp',
  },
  {
    title: 'Мамины рецепты',
    subtitle: 'Что приготовить вдвоём',
    href: 'https://favbox.app/vote/14yk83N3',
    accent: '#d49a3f',
    cover: '/landing/covers/story-recipes.webp',
  },
  {
    title: 'Поиграть вечером',
    subtitle: 'Подборка кооперативных',
    href: 'https://favbox.app/vote/u2N57Ebm',
    accent: '#5a7558',
    cover: '/landing/covers/story-games.webp',
  },
];

export default function Scene10Voting() {
  const reduced = useReducedMotion() ?? false;

  const goToFinal = useCallback(() => {
    tryCtaScrollToFinal();
  }, []);

  const goToStories = useCallback(() => {
    document.getElementById('scene-stories')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <>
      <CinematicScene imageAlt="Маскот с двумя друзьями смотрят карусель сохранённого" imageSrc="/landing/110526/7.webp">
        <h2
          className="font-display font-bold text-white tracking-[-0.02em] leading-[0.96] mb-6"
          style={{ fontSize: 'clamp(2rem, 5.5vw, 3.75rem)', textShadow: '0 4px 24px rgba(0,0,0,0.4)' }}
        >
          Можно<br />
          <span className="text-[#f5a85a]">вдвоём.</span>
        </h2>
        <p
          className="font-body text-white/85 text-base sm:text-lg leading-relaxed mb-8 max-w-md"
          style={{ textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}
        >
          «Выбрать вместе» — отправляешь другу ссылку, он голосует за твои сохранёнки. Видишь, что нравится обоим — и идёте смотреть/играть/ехать.
        </p>

        <div className="flex flex-col items-start gap-5 mt-8">
          <button
            type="button"
            onClick={goToFinal}
            className="group relative inline-flex items-center gap-2.5 px-7 py-4 rounded-2xl bg-[#b7102a] hover:bg-[#991020] active:scale-[0.98] text-white font-semibold text-base transition-all cursor-pointer font-body"
            style={{ boxShadow: '0 16px 40px -8px rgba(183,16,42,0.55)' }}
          >
            <span>Попробовать сейчас</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={goToStories}
            className="group inline-flex items-center gap-2 text-white/80 hover:text-white font-body text-sm font-medium transition-colors"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.35)' }}
          >
            <span>Как это выглядит?</span>
            <motion.svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              animate={reduced ? {} : { y: [0, 4, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            >
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </motion.svg>
          </button>
        </div>
      </CinematicScene>

      {/* ────── Stories-демо на тёмном — продолжает cinematic-настроение ────── */}
      <section id="scene-stories" className="relative w-full bg-[#0d1a0c] overflow-hidden py-16 sm:py-20 px-5 sm:px-8">
        <BgOrbs variant="warm" />

        <div className="relative max-w-6xl mx-auto">
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="text-center max-w-2xl mx-auto mb-10 sm:mb-12"
          >
            <h3
              className="font-display font-bold text-white leading-[1] tracking-[-0.02em] mb-4"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}
            >
              Оцени заметки <span className="text-[#f5a85a]">друзей.</span>
            </h3>
            <p className="font-body text-white/75 text-base leading-relaxed">
              Несколько настоящих подборок ждут — открой и проголосуй, как друг.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-12">
            {STORIES.map((s, i) => (
              <StoryCard key={i} story={s} index={i} />
            ))}
          </div>

          {/* Continue CTA */}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={goToFinal}
              className="group relative inline-flex items-center gap-2.5 px-7 py-4 rounded-2xl bg-[#b7102a] hover:bg-[#991020] active:scale-[0.98] text-white font-semibold text-base transition-all cursor-pointer font-body"
              style={{ boxShadow: '0 12px 32px -8px rgba(183,16,42,0.45)' }}
            >
              <span>Попробовать сейчас</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

/* ────────────── StoryCard ────────────── */

function StoryCard({ story, index }: { story: Story; index: number }) {
  const reduced = useReducedMotion() ?? false;
  const isPlaceholder = story.href === '#';
  const Wrapper = isPlaceholder
    ? ({ children, ...rest }: React.HTMLAttributes<HTMLDivElement>) => <div {...rest}>{children}</div>
    : ({ children, ...rest }: React.HTMLAttributes<HTMLAnchorElement>) => (
        <a href={story.href} target="_blank" rel="noopener noreferrer" {...rest}>
          {children}
        </a>
      );

  const baseDelay = index * 0.14;
  const shineDelay = baseDelay + 0.55;

  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 32, scale: 0.96 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.65, delay: baseDelay, ease: [0.16, 1, 0.3, 1] }}
      className="relative"
    >
    <Wrapper
      className={`group block relative rounded-2xl overflow-hidden border border-[#dce5d9] bg-white transition-all ${
        isPlaceholder ? 'opacity-90 cursor-not-allowed' : 'hover:-translate-y-1 hover:border-[#b8c8b6] cursor-pointer'
      }`}
      style={{ boxShadow: '0 16px 32px -16px rgba(20,40,20,0.18)' }}
    >
      {/* Cover area — реальная обложка 4:5 */}
      <div
        className="relative aspect-[4/5] overflow-hidden"
        style={{ backgroundColor: `${story.accent}22` }}
      >
        <Image
          src={story.cover}
          alt={story.title}
          fill
          sizes="(max-width: 640px) 45vw, (max-width: 1024px) 23vw, 220px"
          className="object-cover"
        />

        {/* Лёгкий accent-tint для общего тона серии */}
        <div
          aria-hidden
          className="absolute inset-0 mix-blend-overlay opacity-25"
          style={{ background: `linear-gradient(160deg, ${story.accent} 0%, transparent 70%)` }}
        />

        {/* Story-progress indicator (декоративный) */}
        <div className="absolute top-2 left-2 right-2 flex gap-1 z-10">
          <div className="flex-1 h-0.5 bg-white/90 rounded-full" />
          <div className="flex-1 h-0.5 bg-white/40 rounded-full" />
          <div className="flex-1 h-0.5 bg-white/40 rounded-full" />
        </div>
      </div>

      {/* Bottom info */}
      <div className="px-3 py-3 sm:px-4 sm:py-3.5">
        <div className="flex items-center gap-1.5 mb-1.5">
          <div className="w-5 h-5 rounded-full bg-[#161d16] flex items-center justify-center">
            <span className="font-display font-bold text-white text-[9px]">D</span>
          </div>
          <span className="font-body text-[#7a8a79] text-[11px]">Dmitry</span>
          {!isPlaceholder && (
            <span className="ml-auto inline-flex items-center gap-0.5 font-mono text-[9px] text-[#b7102a] uppercase tracking-wider">
              live
              <span className="w-1 h-1 rounded-full bg-[#b7102a] animate-pulse" />
            </span>
          )}
        </div>
        <div className="font-display font-bold text-[#161d16] text-[14px] sm:text-[15px] leading-tight mb-0.5">
          {story.title}
        </div>
        <div className="font-body text-[#7a8a79] text-[11px] sm:text-[12px] leading-snug">
          {story.subtitle}
        </div>
      </div>

      {/* hover-arrow для real-link */}
      {!isPlaceholder && (
        <div
          aria-hidden
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={story.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 17L17 7M7 7h10v10" />
          </svg>
        </div>
      )}
      {/* Поочередный блик после выхода карточки */}
      {!reduced && (
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: [0, 1, 0] }}
          viewport={{ once: true }}
          transition={{ duration: 0.95, delay: shineDelay, times: [0, 0.4, 1], ease: 'easeOut' }}
        >
          <motion.div
            className="absolute inset-y-0 w-1/2"
            initial={{ x: '-150%' }}
            whileInView={{ x: '180%' }}
            viewport={{ once: true }}
            transition={{ duration: 0.95, delay: shineDelay, ease: [0.16, 1, 0.3, 1] }}
            style={{
              background:
                'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.75) 50%, transparent 70%)',
              transform: 'skewX(-18deg)',
            }}
          />
        </motion.div>
      )}
    </Wrapper>
    </motion.div>
  );
}
